<template>
  <div>
    <div class="idea-events">
      <create-comment
        :mentionable-users="usersInScope"
        ref="commentEditor"
        :errors="commentErrors"
        :org-id="idea.organizationId"
        :focus-add-comment-on-load="focusAddCommentOnLoad"
        :entity-type="ENTITY_TYPE.IDEA"
        :entity="idea"
        @save="postNewComment"
        @reset-errors="commentErrors = null"
      />
    </div>
    <!--Events-->
    <div class="idea-event-list">
      <event
        v-for="event in events"
        :key="event.id"
        :event="event"
        :idea="idea"
        :mentionable-users="usersInScope"
        @update="updateEvent($event)"
        @delete="deleteEvent($event)"
        @awarded="refreshEvents"
      ></event>
    </div>
  </div>
</template>
<script>
/**
 * This component shows list of activities for idea.
 * It also gives an option to post a comment
 */
import Event from '@/components/ideas/IdeaEvent'
import CreateComment from '@/components/CreateComment'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { MIME_TYPE } from '@/constants/common'
import events from '@/constants/events'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import * as R from 'ramda'
import { DEFAULT_ERROR } from '@/constants/messages'
import { ENTITY_TYPE } from '@/constants/entities'
import { mapActions } from 'vuex'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import { trackEvent } from '../../analytics'

export default {
  name: 'IdeaDiscussion',
  props: ['idea', 'focusAddCommentOnLoad', 'eventToFocus'],
  mixins: [MembershipsMixin, EventBusMixin],
  components: {
    CreateComment,
    Event
  },
  data() {
    return {
      commentErrors: null,
      events: [],
      ENTITY_TYPE
    }
  },
  computed: {
    ideaList() {
      return this.$store.getters['idea/ideaListById'](this.idea.listId)
    },
    usersInScope() {
      let users
      if (this.ideaList) {
        users = this.getUsersInScope(this.team?.id, this.ideaList.organizationId)
      }
      return users
    },
    team() {
      return this.ideaList.teamId ? this.$store.getters.teamById(this.ideaList.teamId) : null
    },
    user() {
      return this.$store.getters.user
    }
  },
  async mounted() {
    const orgId = this.idea.organizationId
    if (this.ideaList) {
      this.updateMemberships(orgId, this.ideaList.teamId, this.team?.isPrivate)
    }
    await this.refreshEvents()
    this.$nextTick(() => {
      this.eventToFocus && this.focusEvent(this.eventToFocus)
    })
    this.listenToEventBus(events.COMMENT_EDITED, this.onCommentChange)
    this.listenToEventBus(events.COMMENT_DELETED, this.onCommentChange)
    this.listenToEventBus(events.COMMENT_ADDED, this.onCommentChange)
  },
  methods: {
    ...mapActions('idea', ['getIdeaEvents', 'createIdeaEvent']),
    async postNewComment(comment) {
      if (comment) {
        try {
          const event = await this.createIdeaEvent({
            id: this.idea.id,
            comment,
            commentType: MIME_TYPE.HTML,
            orgId: this.idea.organizationId,
            actorId: this.user.id
          })
          this.$refs.commentEditor.$refs.editor.setData(null)
          this.events = [event, ...this.events]
          this.commentErrors = null
          EventBus.$emit(events.COMMENT_ADDED, { type: 'idea', value: this.idea })
          EventBus.$emit('ADD_TOAST', { message: 'Comment has been posted successfully' })
        } catch (e) {
          let errors = R.path(['response', 'data', 'detail', 'comment'], e)
          if (!errors) {
            const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
            errors = [message]
          }
          this.commentErrors = errors
        }
        trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.NEW_COMMENT, EVENT_NAME.IDEA)
      }
    },
    updateEvent(event) {
      const eventIndex = this.events.findIndex(x => x.id === event.id)
      this.events = R.assocPath([eventIndex], event, this.events)
      EventBus.$emit(events.COMMENT_EDITED, { type: 'idea', value: this.idea })
    },
    deleteEvent(event) {
      this.events = this.events.filter(ev => ev.id !== event.id)
      EventBus.$emit(events.COMMENT_DELETED, { type: 'idea', value: this.idea })
    },
    async refreshEvents() {
      const events = await this.getIdeaEvents({
        id: this.idea.id,
        orgId: this.idea.organizationId
      })
      this.events = events
    },
    onCommentChange({ type, value: idea }) {
      if (type === 'idea' && idea.id === this.idea.id) {
        this.refreshEvents()
      }
    },
    focusEvent(eventId) {
      const elem = document.querySelector(`#event-${eventId}`)
      elem?.scrollIntoView()
    },
    onIdeaUpdate(idea) {
      if (idea.id === this.idea.id) {
        this.refreshEvents()
      }
    }
  },
  watch: {
    idea(before, after) {
      if (before && after && before.id !== after.id) {
        this.refreshEvents()
      }
    },
    ideaList(current, earlier) {
      if (current && !earlier) {
        this.updateMemberships(current.organizationId, current.teamId, this.team?.isPrivate)
      }
    },
    async eventToFocus(eventToFocus) {
      if (eventToFocus) {
        const event = this.events.find(x => x.id === +eventToFocus)
        if (!event) {
          await this.refreshEvents()
        }
        this.$nextTick(() => this.focusEvent(eventToFocus))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

.idea-event-list,
.idea-events {
  margin-bottom: 24px;
}

@include media-breakpoint-down(md) {
  .idea-event-list,
  .idea-events {
    padding-left: 4px;
    margin-top: 8px !important;
    margin-bottom: 0;
  }
}
</style>
